import React, { useEffect, useState } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton,
    Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { LeaveAllocationService } from '../../services/leave-allocation.service';
import { UserService } from '../../services/user.service';
import { LeaveTypeService } from '../../services/leave-type.service';
import { UserLeaveAllocation, LeaveTypeConfig } from '../../interfaces/leave.interface';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { AuthService } from '../../services/auth.service';
import { displayUsername } from '../../utils/userUtils';

interface SimpleUser {
    id: string;
    username: string;
}

const UserLeaveAllocationManagement: React.FC = () => {
    const [allocations, setAllocations] = useState<UserLeaveAllocation[]>([]);
    const [users, setUsers] = useState<SimpleUser[]>([]);
    const [leaveTypes, setLeaveTypes] = useState<LeaveTypeConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [fiscalYear, setFiscalYear] = useState<number>(new Date().getFullYear());
    const [selectedUser, setSelectedUser] = useState<SimpleUser | null>(null);
    const [userInput, setUserInput] = useState('');
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const [leaveType, setLeaveType] = useState<string>('');
    const [numberOfLeaves, setNumberOfLeaves] = useState<number>(0);
    const toast = useToast();
    const { t } = useTranslation(['leave', 'common']);

    useEffect(() => {
        LeaveTypeService.getByInstitution(true).then((data) => {
            setLeaveTypes(data);
            if (data.length > 0) setLeaveType(data[0].name);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        fetchAllocations();
    }, [fiscalYear]);

    useEffect(() => {
        if (!userInput.trim()) {
            setUsers([]);
            return;
        }
        const handle = setTimeout(async () => {
            setUserSearchLoading(true);
            try {
                const data = await UserService.getInstitutionUsers(0, 20, undefined, userInput);
                setUsers(data.content || []);
            } catch (err) {
                toast.showApiError(err, t('userLeaveAllocationManagement.errors.searchUsers'));
            } finally {
                setUserSearchLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInput]);

    const fetchAllocations = async () => {
        try {
            setLoading(true);
            const data = await LeaveAllocationService.getAllocationsByInstitutionAndFiscalYear(fiscalYear);
            setAllocations(data);
        } catch (err) {
            toast.showApiError(err, t('userLeaveAllocationManagement.errors.fetch'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrUpdate = async () => {
        if (!selectedUser) {
            toast.showError(t('userLeaveAllocationManagement.selectUser'));
            return;
        }
        try {
            await LeaveAllocationService.createOrUpdate({
                institutionId: AuthService.getSelectedInstitution()!!,
                userId: selectedUser.id,
                leaveType,
                numberOfLeaves,
                fiscalYear
            });
            setDialogOpen(false);
            await fetchAllocations();
        } catch (err) {
            toast.showApiError(err, t('userLeaveAllocationManagement.errors.save'));
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm(t('userLeaveAllocationManagement.confirmDelete'))) {
            try {
                await LeaveAllocationService.deleteAllocation(id);
                await fetchAllocations();
            } catch (err) {
                toast.showApiError(err, t('userLeaveAllocationManagement.errors.delete'));
            }
        }
    };

    const handleEditClick = (allocation: UserLeaveAllocation) => {
        const preFilled: SimpleUser = { id: allocation.userId, username: allocation.userName };
        setSelectedUser(preFilled);
        setUsers([preFilled]);
        setUserInput('');
        setLeaveType(allocation.leaveType);
        setNumberOfLeaves(allocation.numberOfLeaves);
        setDialogOpen(true);
    };

    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

    if (loading) return <Typography>{t('loading')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>{t('userLeaveAllocationManagement.title')}</Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 3 }}>
                <TextField
                    select
                    label={t('userLeaveAllocationManagement.fiscalYear')}
                    value={fiscalYear}
                    onChange={(e) => setFiscalYear(Number(e.target.value))}
                    sx={{ minWidth: 150 }}
                    size="small"
                >
                    {yearOptions.map((year) => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                </TextField>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" onClick={() => {
                    setSelectedUser(null);
                    setUserInput('');
                    setUsers([]);
                    setLeaveType(leaveTypes[0]?.name ?? '');
                    setNumberOfLeaves(0);
                    setDialogOpen(true);
                }}>
                    {t('userLeaveAllocationManagement.addOverride')}
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('userLeaveAllocationManagement.columns.user')}</TableCell>
                            <TableCell>{t('userLeaveAllocationManagement.columns.leaveType')}</TableCell>
                            <TableCell>{t('userLeaveAllocationManagement.columns.allocatedLeaves')}</TableCell>
                            <TableCell>{t('userLeaveAllocationManagement.columns.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allocations.map((alloc) => (
                            <TableRow key={alloc.id}>
                                <TableCell>{displayUsername(alloc.userName)}</TableCell>
                                <TableCell>{alloc.leaveType}</TableCell>
                                <TableCell>{alloc.numberOfLeaves}</TableCell>
                                <TableCell>
                                    <Button size="small" onClick={() => handleEditClick(alloc)}>{t('common:actions.edit')}</Button>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(alloc.id)} aria-label={t('userLeaveAllocationManagement.deleteAriaLabel')}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {allocations.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">{t('userLeaveAllocationManagement.empty')}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('userLeaveAllocationManagement.dialogTitle')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Autocomplete
                            options={users}
                            getOptionLabel={(option) => displayUsername(option.username)}
                            value={selectedUser}
                            onChange={(_, newValue) => setSelectedUser(newValue)}
                            onInputChange={(_, value, reason) => {
                                if (reason === 'input') {
                                    setUserInput(value);
                                } else if (reason === 'clear') {
                                    setUserInput('');
                                }
                            }}
                            filterOptions={(x) => x}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            loading={userSearchLoading}
                            noOptionsText={userInput ? t('userLeaveAllocationManagement.noUsersFound') : t('userLeaveAllocationManagement.typeToSearch')}
                            fullWidth
                            renderInput={(params) => (
                                <TextField {...params} label={t('userLeaveAllocationManagement.userLabel')} placeholder={t('userLeaveAllocationManagement.searchUsersPlaceholder')} />
                            )}
                        />
                        <TextField
                            select
                            label={t('userLeaveAllocationManagement.leaveTypeLabel')}
                            value={leaveType}
                            onChange={(e) => setLeaveType(e.target.value)}
                            fullWidth
                        >
                            {leaveTypes.map((lt) => (
                                <MenuItem key={lt.id} value={lt.name}>{lt.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={t('userLeaveAllocationManagement.numberOfLeavesLabel')}
                            type="number"
                            value={numberOfLeaves}
                            onChange={(e) => setNumberOfLeaves(Number(e.target.value))}
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" onClick={handleCreateOrUpdate}>{t('common:actions.save')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserLeaveAllocationManagement;
